/**
 * Sends Connect cannot vouch for, and the way to put them right (`docs/29`).
 *
 * A gateway that answers a batch "yes" without naming its messages, or never
 * answers, leaves Connect unsure what went — and such messages are never sent
 * again on their own, because a resend could reach the same person twice. The
 * vendor's own DLR export says what it took: uploaded here, it is matched on
 * phone and text, previewed, and only then applied — ids taken, each message
 * charged once, and what never reached the vendor sent again within the same
 * send.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCheck2 } from 'lucide-react';
import {
  ApiError,
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  api,
  formatDateTime,
  formatNumber,
  toast,
  useCan,
} from '@connect/web-shared';

interface PendingSend {
  public_id: string;
  client: string;
  template_name: string;
  created_at: string;
  recipient_count: number;
  in_doubt: number;
}

interface Counted {
  messages: number;
  parts: number;
}

interface Reconciliation {
  send: string;
  client: string;
  in_doubt: number;
  found_at_vendor: number;
  charged_now: Counted;
  returned: Counted;
  to_send_again: Counted;
  applied: boolean;
}

export const reconciliationKey = ['messaging', 'reconciliation'] as const;

function reconcile(sendId: string, file: File, apply: boolean) {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('apply', apply ? 'true' : 'false');
  return api.upload<Reconciliation>(`/messaging/reconciliation/${sendId}`, form);
}

export function SendReconciliationCard() {
  const canReconcile = useCan('messaging.credit_adjustment.create');
  const pending = useQuery({
    queryKey: reconciliationKey,
    queryFn: () => api.get<PendingSend[]>('/messaging/reconciliation'),
  });
  const [open, setOpen] = useState<PendingSend | null>(null);

  if (!pending.data?.length) return null;
  return (
    <Card>
      <CardHeader
        title="Sends to reconcile"
        description="Messages the gateway did not confirm. They are never sent again on their own — the vendor's own report says which went."
      />
      <CardBody>
        <table className="w-full text-sm">
          <tbody>
            {pending.data.map((row) => (
              <tr key={row.public_id} className="border-t border-line first:border-t-0">
                <td className="py-2 pr-3">
                  <p className="font-medium text-heading">{row.client}</p>
                  <p className="text-xs text-muted">
                    {row.template_name} · {formatDateTime(row.created_at)}
                  </p>
                </td>
                <td className="py-2 pr-3 text-right tabular">
                  {formatNumber(row.in_doubt)} of {formatNumber(row.recipient_count)} in doubt
                </td>
                <td className="py-2 text-right">
                  {canReconcile ? (
                    <Button size="sm" variant="secondary" onClick={() => setOpen(row)}>
                      <FileCheck2 className="size-4" />
                      Reconcile
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
      {open ? <ReconcileDialog send={open} onClose={() => setOpen(null)} /> : null}
    </Card>
  );
}

function ReconcileDialog({ send, onClose }: { send: PendingSend; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const preview = useMutation({ mutationFn: (chosen: File) => reconcile(send.public_id, chosen, false) });
  const apply = useMutation({
    mutationFn: (chosen: File) => reconcile(send.public_id, chosen, true),
    onSuccess: (done) => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKey });
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'health'] });
      toast.success(
        'Send reconciled',
        done.to_send_again.messages
          ? `${formatNumber(done.to_send_again.messages)} going out again within the same send.`
          : 'Every message is accounted for.',
      );
      onClose();
    },
  });
  const error = preview.error ?? apply.error;
  const result = preview.data;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Reconcile ${send.client}'s send`}
      description="At the vendor, open the client's account → DLR Report → Download Excel, and upload that file here."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!file || !result || apply.isPending}
            loading={apply.isPending}
            onClick={() => file && apply.mutate(file)}
          >
            Apply
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="The vendor's DLR export"
          onChange={(event) => {
            const chosen = event.target.files?.[0] ?? null;
            setFile(chosen);
            preview.reset();
            if (chosen) preview.mutate(chosen);
          }}
        />
        {error ? (
          <Alert tone="danger" title="Not done">
            {error instanceof ApiError ? error.message : 'Could not reach the server.'}
          </Alert>
        ) : null}
        {preview.isPending ? <p className="text-sm text-muted">Matching…</p> : null}
        {result ? (
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-muted">In doubt</dt>
            <dd className="tabular">{formatNumber(result.in_doubt)}</dd>
            <dt className="text-muted">Found at the vendor</dt>
            <dd className="tabular">{formatNumber(result.found_at_vendor)}</dd>
            <dt className="text-muted">Charged now (went, never charged)</dt>
            <dd className="tabular">
              {formatNumber(result.charged_now.messages)} · {formatNumber(result.charged_now.parts)} credits
            </dd>
            <dt className="text-muted">Returned (charged, never went)</dt>
            <dd className="tabular">
              {formatNumber(result.returned.messages)} · {formatNumber(result.returned.parts)} credits
            </dd>
            <dt className="text-muted">Sent again, within the same send</dt>
            <dd className="tabular">{formatNumber(result.to_send_again.messages)}</dd>
          </dl>
        ) : null}
      </div>
    </Modal>
  );
}
